import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PauseCircle, PlayCircle, XCircle, Clock, RotateCcw, CalendarPlus, CheckCircle2, Send, Lock, Unlock } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export const STAGES = {
  shortlisted: ["Shortlisted", "text-slate-200 border-white/30 bg-white/5"],
  docs_submitted: ["Document Review", "text-sky-300 border-sky-500/50 bg-sky-500/10"],
  docs_approved: ["Docs Approved · Terms Pending", "text-fuchsia-300 border-fuchsia-500/50 bg-fuchsia-500/10"],
  on_hold: ["On Hold", "text-amber-300 border-amber-500/50 bg-amber-500/10"],
  pending_review: ["Pending Review", "text-sky-300 border-sky-500/50 bg-sky-500/10"],
  letter_sent: ["Letter Sent", "text-nx-goldlight border-nx-gold/50 bg-nx-gold/10"],
  confirmed: ["Confirmed", "text-emerald-300 border-emerald-500/50 bg-emerald-500/10"],
  cancelled: ["Cancelled", "text-red-300 border-red-500/50 bg-red-500/10"],
};

export const stageOf = (l) => {
  if (l?.flow !== "v2") return null;
  const rv = l.review?.status;
  if (rv === "cancelled" || l.stage === "cancelled") return "cancelled";
  if (rv === "on_hold") return "on_hold";
  if (rv === "pending_review") return "pending_review";
  return l.stage || "shortlisted";
};

export const StageBadge = ({ letter, testId = "stage-badge" }) => {
  const s = stageOf(letter);
  if (!s) return null;
  const [label, cls] = STAGES[s];
  return <span className={`inline-flex items-center text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${cls}`} data-testid={testId}>{label}</span>;
};

export const ReviewBadge = ({ review, testId = "review-badge" }) => {
  if (!review?.status) return null;
  const map = {
    on_hold: ["On Hold", "text-amber-300 border-amber-500/50 bg-amber-500/10", PauseCircle],
    pending_review: ["Pending Review", "text-sky-300 border-sky-500/50 bg-sky-500/10", RotateCcw],
    cancelled: ["Cancelled", "text-red-300 border-red-500/50 bg-red-500/10", XCircle],
    expired: ["Hold Expired — Not Submitted", "text-red-300 border-red-500/50 bg-red-500/10", Clock],
  };
  const [label, cls, Icon] = map[review.status] || [review.status, "text-slate-300 border-white/20", Clock];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${cls}`} data-testid={testId} title={review.reason || ""}>
      <Icon size={11} /> {label}
      {review.status === "on_hold" && review.expires_at && <span className="normal-case tracking-normal text-slate-400">· link till {fmt(review.expires_at)}</span>}
      {review.status === "cancelled" && review.auto && <span className="normal-case tracking-normal text-slate-400">· auto (no response in time)</span>}
    </span>
  );
};

export const AppointmentBadge = ({ appointment, testId = "appointment-badge" }) => {
  if (!appointment) return null;
  const rv = appointment.review;
  if (rv?.status === "on_hold") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border px-2.5 py-1 text-amber-300 border-amber-500/50 bg-amber-500/10 max-w-full flex-wrap" data-testid={testId} title={rv.reason || ""}>
        <span className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap"><PauseCircle size={11} className="shrink-0" /> On Hold</span>
        {rv.reason && <span className="normal-case tracking-normal text-amber-100/90 truncate max-w-[260px]">— {rv.reason}</span>}
        {rv.expires_at && <span className="normal-case tracking-normal text-slate-300 shrink-0">· until {fmt(rv.expires_at)}</span>}
        {rv.kind === "letter" && <span className="normal-case tracking-normal text-slate-400 shrink-0">· letter on hold</span>}
        {rv.held_at && <span className="normal-case tracking-normal text-slate-400 shrink-0">· {Math.floor((Date.now() - new Date(rv.held_at).getTime()) / 86400000)}d on hold</span>}
      </span>
    );
  }
  if (rv?.status === "cancelled" || appointment.stage === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border px-2.5 py-1 text-red-300 border-red-500/50 bg-red-500/10 max-w-full flex-wrap" data-testid={testId} title={rv?.reason || ""}>
        <span className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap"><XCircle size={11} className="shrink-0" /> Appointment Cancelled</span>
        {rv?.reason && <span className="normal-case tracking-normal text-red-100/80 truncate max-w-[260px]">— {rv.reason}</span>}
        {rv?.auto && <span className="normal-case tracking-normal text-slate-400 shrink-0">· auto</span>}
      </span>
    );
  }
  if (rv?.status === "pending_review") {
    return <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border px-2.5 py-1 text-sky-300 border-sky-500/50 bg-sky-500/10" data-testid={testId}><RotateCcw size={11} /> Docs re-uploaded · Pending Review</span>;
  }
  return null;
};

export const letterIssued = (l) => l?.status === "approved" || l?.stage === "letter_sent" || l?.stage === "confirmed";

export const HoldAge = ({ review, testId = "hold-age" }) => {
  if (review?.status !== "on_hold" || !review?.held_at) return null;
  const days = Math.floor((Date.now() - new Date(review.held_at).getTime()) / 86400000);
  const long = days >= 7;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${long ? "text-red-300 border-red-500/50 bg-red-500/10" : "text-amber-200 border-amber-500/40 bg-amber-500/5"}`} data-testid={testId} title={`On hold since ${fmt(review.held_at)}`}>
      <Clock size={11} /> On hold for {days === 0 ? "less than a day" : `${days} day${days === 1 ? "" : "s"}`}
    </span>
  );
};

export const ApplicantHoldButton = ({ api, application, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const name = application.name || `${application.first_name || ""} ${application.surname || ""}`.trim();
  if ((application.status || "new") === "closed" || application.appointment?.review?.status === "on_hold" || application.appointment?.stage === "cancelled") return null;
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason.");
    setBusy(true);
    try {
      const { data } = await api.post(`/letters/appointment/from-application/${application.id}/hold`, { reason: reason.trim() });
      toast.success(`${name} placed on hold — emailed a ${data.review?.window || "7 days"} upload link${tgNote(data.telegram)}${data.terms_pending ? ". Complete the terms in Appointment Letters before approving." : ""}`);
      tgWarn(data.telegram);
      setOpen(false);
      setReason("");
      onDone?.(data);
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not place on hold.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 px-3 py-1.5 transition-colors" data-testid={`application-hold-${application.id}`} title="Put this applicant on hold and email a 7-day document link">
        <PauseCircle size={12} /> Hold
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid="applicant-hold-modal">
          <div className="bg-nx-navy2 border border-white/15 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-sm font-medium flex items-center gap-2"><PauseCircle size={15} className="text-amber-300" /> Put {name} on hold</h3>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">The applicant is shortlisted and emailed this reason with a secure upload link valid for 7 days. You'll get a Telegram reminder every 24 hours until they upload; no upload in 7 days auto-cancels. An appointment record is created with terms pending — HR completes designation, salary and joining date later.</p>
            <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={1000} placeholder="Reason for hold (e.g. Please upload your experience certificate and last 3 payslips)" className="mt-3 w-full bg-white/[0.04] border border-white/15 text-slate-100 text-sm px-3 py-2 focus:outline-none focus:border-nx-goldlight" data-testid="applicant-hold-reason" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy} className="text-[11px] tracking-[0.12em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white">Back</button>
              <button onClick={submit} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border border-amber-500/60 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50" data-testid="applicant-hold-submit">
                {busy && <Loader2 size={12} className="animate-spin" />} Send hold notice
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const tgNote = (t) => {
  if (!t) return "";
  if (t.ok) return ` · CEO informed on Telegram (${t.sent.length} chat${t.sent.length === 1 ? "" : "s"})`;
  if (t.sent?.length) return ` · Telegram partly delivered (${t.sent.length} ok, ${t.failed.length} failed)`;
  return "";
};
const tgWarn = (t) => {
  if (!t || t.ok) return;
  if (!t.sent?.length) toast.warning("Telegram confirmation NOT delivered to the CEO — check CEO_TELEGRAM_CHAT_IDS in the server secrets.", { duration: 9000 });
};

export const ReviewActions = ({ api, letterId, review, onDone, letter }) => {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const stage = stageOf(letter);
  const canApprove = letter?.flow === "v2" && !letter?.terms_pending && (stage === "docs_submitted" || stage === "pending_review");
  if (review?.status === "cancelled" || stage === "cancelled" || stage === "letter_sent" || stage === "confirmed") return null;

  const approve = async () => {
    if (!window.confirm("Approve all documents? The appointment letter will be generated and emailed to the candidate with a 24-hour acceptance link.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/letters/appointment/${letterId}/approve-docs`);
      toast.success(data?.needs_terms ? "Documents approved — now enter the employment terms in Team Tracker to generate the appointment letter" : "Documents approved — appointment letter emailed to the candidate", { duration: 8000 });
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not approve.");
    } finally {
      setBusy(false);
    }
  };

  const extend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/candidate-review/admin/${letterId}/extend`);
      toast.success(`Hold extended — link now valid till ${fmt(data.expires_at)}. Candidate emailed.`);
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not extend the hold.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason.");
    setBusy(true);
    try {
      const { data } = await api.post(`/candidate-review/admin/${letterId}/${mode}`, { reason: reason.trim() });
      toast.success(`${mode === "hold" ? `Appointment placed on hold — candidate emailed a ${data.review?.window || "7 days"} upload link` : "Appointment cancelled — candidate emailed"}${tgNote(data.telegram)}`);
      tgWarn(data.telegram);
      setMode(null);
      setReason("");
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="review-actions">
        {canApprove && (
          <button onClick={approve} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50" data-testid="review-approve-btn" title="Approve documents and email the appointment letter">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} <span className="hidden sm:inline">Approve</span>
          </button>
        )}
        {(review?.status === "on_hold" || review?.status === "pending_review") && review?.token !== null && (
          <button onClick={extend} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50" data-testid="review-extend-btn" title="Add 3 more days to the upload link">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CalendarPlus size={12} />} <span className="hidden sm:inline">Extend +3d</span>
          </button>
        )}
        {review?.status !== "on_hold" && (
          <button onClick={() => setMode("hold")} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="review-hold-btn">
            <PauseCircle size={12} /> <span className="hidden sm:inline">Hold</span>
          </button>
        )}
        <button onClick={() => setMode("cancel")} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-red-500/50 text-red-300 hover:bg-red-500/10" data-testid="review-cancel-btn">
          <XCircle size={12} /> <span className="hidden sm:inline">Cancel</span>
        </button>
      </div>
      {mode && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => !busy && setMode(null)} data-testid="review-reason-modal">
          <div className="bg-nx-navy2 border border-white/15 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-sm font-medium flex items-center gap-2">
              {mode === "hold" ? <><PauseCircle size={15} className="text-amber-300" /> Put appointment on hold</> : <><XCircle size={15} className="text-red-300" /> Cancel appointment</>}
            </h3>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              {mode === "hold"
                ? `The candidate will receive an email with this reason and a secure upload link valid for exactly ${letterIssued(letter) ? "24 hours (joined-staff rule: 12-hour CEO reminder, final alert on expiry — no cancellation)" : "7 days (daily CEO Telegram reminders, auto-cancel on expiry)"}.`
                : "The candidate will immediately receive an email with this reason. The appointment is cancelled permanently and no upload link is provided."}
            </p>
            <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={1000} placeholder={mode === "hold" ? "Reason for hold (e.g. Aadhaar copy is unreadable — please upload a clear scan)" : "Reason for cancellation"}
              className="mt-3 w-full bg-white/[0.04] border border-white/15 text-slate-100 text-sm px-3 py-2 focus:outline-none focus:border-nx-goldlight" data-testid="review-reason-input" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setMode(null)} disabled={busy} className="text-[11px] tracking-[0.12em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white" data-testid="review-reason-cancel">Back</button>
              <button onClick={submit} disabled={busy} className={`flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border disabled:opacity-50 ${mode === "hold" ? "border-amber-500/60 text-amber-200 hover:bg-amber-500/10" : "border-red-500/60 text-red-200 hover:bg-red-500/10"}`} data-testid="review-reason-submit">
                {busy && <Loader2 size={12} className="animate-spin" />} {mode === "hold" ? "Send hold notice" : "Cancel appointment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const LetterReviewActions = ({ api, letter, onDone }) => {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const rv = letter?.review || {};
  if (!letterIssued(letter) || rv.status === "cancelled" || letter?.stage === "cancelled") return null;
  const onHold = rv.status === "on_hold";
  const letterHold = onHold && rv.kind === "letter";

  const resume = async () => {
    if (!window.confirm(`Lift the hold on ${letter.employee_name}'s appointment letter? The candidate will be emailed that the appointment stands.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/candidate-review/admin/${letter.id}/letter-resume`);
      toast.success(`Hold lifted — candidate emailed${tgNote(data.telegram)}`);
      tgWarn(data.telegram);
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not lift the hold.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason.");
    setBusy(true);
    try {
      const { data } = await api.post(`/candidate-review/admin/${letter.id}/${mode === "hold" ? "letter-hold" : "cancel"}`, { reason: reason.trim() });
      toast.success(`${mode === "hold" ? "Appointment letter placed on hold" : "Appointment cancelled"} — candidate emailed${tgNote(data.telegram)}`);
      tgWarn(data.telegram);
      setMode(null);
      setReason("");
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {letterHold ? (
        <button onClick={resume} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50" data-testid="letter-resume-btn" title="Lift the hold — the appointment stands">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Resume
        </button>
      ) : !onHold && (
        <button onClick={() => setMode("hold")} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-50" data-testid="letter-hold-btn" title="Put the issued appointment letter on hold">
          <PauseCircle size={12} /> Hold
        </button>
      )}
      <button onClick={() => setMode("cancel")} disabled={busy} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-red-500/50 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50" data-testid="letter-cancel-btn" title="Cancel the issued appointment letter">
        <XCircle size={12} /> Cancel
      </button>
      {mode && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => !busy && setMode(null)} data-testid="letter-review-modal">
          <div className="bg-nx-navy2 border border-white/15 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-sm font-medium flex items-center gap-2">
              {mode === "hold" ? <><PauseCircle size={15} className="text-amber-300" /> Put {letter.employee_name}'s appointment letter on hold</> : <><XCircle size={15} className="text-red-300" /> Cancel {letter.employee_name}'s appointment</>}
            </h3>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              {mode === "hold"
                ? `${letter.ref} · The candidate is emailed immediately that the appointment letter is on hold, with this reason. You get a Telegram confirmation on both CEO accounts. No upload link and no auto-cancel — the hold stays until you Resume or Cancel.`
                : `${letter.ref} · The candidate is emailed immediately that the appointment is cancelled, with this reason. You get a Telegram confirmation on both CEO accounts. This is final.`}
            </p>
            <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={1000} placeholder={mode === "hold" ? "Reason for hold (e.g. Joining date under review pending project allocation)" : "Reason for cancellation"}
              className="mt-3 w-full bg-white/[0.04] border border-white/15 text-slate-100 text-sm px-3 py-2 focus:outline-none focus:border-nx-goldlight" data-testid="letter-review-reason" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setMode(null)} disabled={busy} className="text-[11px] tracking-[0.12em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white" data-testid="letter-review-back">Back</button>
              <button onClick={submit} disabled={busy} className={`flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border disabled:opacity-50 ${mode === "hold" ? "border-amber-500/60 text-amber-200 hover:bg-amber-500/10" : "border-red-500/60 text-red-200 hover:bg-red-500/10"}`} data-testid="letter-review-submit">
                {busy && <Loader2 size={12} className="animate-spin" />} {mode === "hold" ? "Put on hold" : "Cancel appointment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const TelegramTestButton = ({ api }) => {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/candidate-review/admin/telegram-test");
      setLast(data);
      if (data.ok) toast.success(`Telegram delivered to ${data.sent.length} CEO chat${data.sent.length === 1 ? "" : "s"}: ${data.sent.join(", ")}`);
      else if (!data.chats?.length) toast.error("No CEO chat IDs configured — set CEO_TELEGRAM_CHAT_IDS in the server secrets.", { duration: 9000 });
      else toast.error(`Telegram failed for ${data.failed.join(", ")}${data.sent.length ? ` · delivered to ${data.sent.join(", ")}` : ""}`, { duration: 9000 });
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Telegram test failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="telegram-test">
      {last && (
        <span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${last.ok ? "text-emerald-300 border-emerald-500/50" : "text-red-300 border-red-500/50"}`} data-testid="telegram-test-result">
          {last.ok ? `${last.sent.length}/${last.chats.length} delivered` : last.chats?.length ? `${last.failed.length} failed` : "no chat IDs"}
        </span>
      )}
      <button onClick={run} disabled={busy} className="flex items-center gap-2 whitespace-nowrap text-xs tracking-[0.14em] uppercase px-4 py-3 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 transition-colors disabled:opacity-50" data-testid="telegram-test-btn" title="Send a test message to both CEO Telegram accounts">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Test Telegram
      </button>
    </div>
  );
};

export const AccessBadge = ({ access, testId = "access-badge" }) => {
  if (!access?.status || access.status === "nothing_to_restore") return null;
  const map = {
    suspended: ["Access Suspended", "text-amber-300 border-amber-500/50 bg-amber-500/10", Lock],
    revoked: ["Access Revoked", "text-red-300 border-red-500/50 bg-red-500/10", Lock],
    restored: ["Access Restored", "text-emerald-300 border-emerald-500/50 bg-emerald-500/10", Unlock],
  };
  const [label, cls, Icon] = map[access.status] || [access.status, "text-slate-300 border-white/20", Lock];
  const rows = access.status === "restored" ? access.restore_results : access.results;
  const title = (rows || []).map((r) => `${r.ok === null ? "–" : r.ok ? "✓" : "!"} ${r.system}: ${r.detail}`).join("\n");
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border px-2.5 py-1 ${cls}`} data-testid={testId} title={title}>
      <Icon size={11} /> {label}
      <span className="normal-case tracking-normal text-slate-400">· {fmt(access.status === "restored" ? access.restored_at : access.at)}</span>
    </span>
  );
};
